import styled, {
  css,
  StyledComponent,
  isStyledComponent,
} from 'styled-components';

import { SizeUnit } from '@utils/types';

import { widthMixin } from '../mixins';

type InputContainerProps = {
  hasError?: boolean;
  width?: SizeUnit;
  value?: unknown;
  useNative?: boolean;
  focusOpacity?: number;
  height: number;
  labelTop?: number;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  InputComponent: StyledComponent<any, any>;
};

export const InputStyles = css`
  height: 34px;
  font-size: 16px;
  padding: 0;
  background-color: transparent;
  box-shadow: none;
  border: none;
  outline: none;
  width: 100%;
  font-weight: 400;
  &:focus {
    font-weight: 600;
    position: relative;
  }
`;

export const SeturInput = styled.input<{ customCss?: typeof InputStyles }>`
  ${InputStyles};
  && {
    input {
      background-color: transparent;
    }
  }
`;

export const InputLabel = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.palette.darkGrey};
  opacity: 0.5;
  transition: all 0.1s ease;
  position: absolute;
  height: 16px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const labelFocus = (opacity?: number, labelTop?: number) => css`
  top: ${labelTop}px;
  font-size: 12px;
  font-weight: bold;
  z-index: 0;
  opacity: ${opacity};
`;
const labelWhenValueMixin = ({
  value,
  focusOpacity,
  useNative,
  height,
  labelTop,
}: InputContainerProps) =>
  useNative
    ? null
    : (() => {
        if (value) {
          return labelFocus(focusOpacity, labelTop);
        }
        return css`
          top: ${(height - 16) / 2}px;
          left: 15px;
          padding: 0;
        `;
      })();

const inputWhenValueMixin = ({ value, useNative }: InputContainerProps) => {
  if (useNative) return null;

  return value
    ? css`
        position: relative;
        top: 8px;
        font-weight: 600;
        height: 22px;
      `
    : css``;
};

const nativePlaceholderMixin = ({ useNative }: InputContainerProps) => {
  if (useNative) {
    return css`
      ${InputLabel} {
        display: none;
      }
      & input::placeholder,
      & input::-webkit-input-placeholder {
        font-weight: normal;
        opacity: 0.8;
      }
    `;
  }
  return css`
    & input::placeholder,
    & input::-webkit-input-placeholder {
      color: transparent;
    }
  `;
};
export const InputContainer = styled.div<InputContainerProps>`
  height: ${(p) => p.height}px;
  padding: 15px 15px 13px 15px;
  color: ${({ theme }) => theme.palette.brownishGrey};
  background-color: ${({ theme }) => theme.palette.lynxWhite};
  position: relative;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.palette.tomato : theme.palette.inputBorderColor};

  &:focus-within {
    border: 1px solid
      ${({ theme, hasError }) =>
        hasError ? theme.palette.tomato : theme.palette.inputBorderColorActive};
    ${InputLabel} {
      ${({ focusOpacity = 1, labelTop }) => labelFocus(focusOpacity, labelTop)};
    }
  }
  ${InputLabel} {
    ${labelWhenValueMixin}
  }
  border-radius: 8px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  ${SeturInput}:focus + ${InputLabel} {
    opacity: 1;
  }
  ${SeturInput} {
    &,
    &:focus,
    &:hover {
      color: ${({ theme, hasError }) =>
        hasError ? theme.palette.tomato : theme.palette.matterHorn};
      outline: none;
      box-shadow: none;
      background-color: transparent;
    }

    ${inputWhenValueMixin}
    &:focus, &:focus-within {
      font-weight: 600;
      position: relative;
      ${(p) =>
        p.useNative
          ? null
          : css`
              top: 8px;
              height: 22px;
            `}
    }
  }
  & ${(p) => p.InputComponent} {
    ${InputStyles};
    &,
    &:focus,
    &:hover {
      color: ${({ theme, hasError }) =>
        hasError ? theme.palette.tomato : theme.palette.matterHorn};
    }
    ${inputWhenValueMixin}
  }
  transition: all 0.5s ease;
  ${widthMixin};
  ${nativePlaceholderMixin};
  box-sizing: border-box;
  max-width: 100%;
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
  input {
    ${(p) => p.theme.mediaQueries.PHONE} {
      font-size: 16px;
    }
  }
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.PHONE})`} {
    height: 58px;
  }
`;

export const IconContainer = styled.span`
  flex: none;
  display: flex;
  align-items: center;
  margin-left: 4px;
`;

export const InputWrapper = styled.span`
  padding: 0px;
  background-color: transparent;
  box-shadow: none;
  border: none;
  outline: none;
  position: relative;
  display: inline-flex;
`;

export { isStyledComponent, styled };
